import React from 'react';
import type { VariantProps } from 'class-variance-authority';
import { useAtom } from 'jotai';
import { cn } from '../../../../utils/cn';
import { ReactComponent as SearchIcon } from '../../../../icons/svg/search.svg';
import { ReactComponent as LogomarkWhite } from '../../../../icons/svg/logo-new-white.svg';
import { ReactComponent as LogomarkBlack } from '../../../../icons/svg/logo-new-black.svg';
import { ReactComponent as ArchiproWordmark } from '../../../../icons/svg/logo-text.svg';
import { CountrySelectMenu } from '../CountrySelectMenu';
import { UserCart } from './UserCart';
import { DesktopUserMenu } from './DesktopUserMenu';
import { WarningBanner } from '../../../warning-banner/components/WarningBanner';
import { ActiveMarker } from '../../../../components/active-marker';
import { RoccoLink } from '../../../../components/link/RoccoLink';
import { uiStateAtomSearchClicked } from '../../states/ui-states-search-clicked';
import {
    _headerVariants,
    type HeaderCartInfo,
    type HeaderCountryElement,
    type HeaderCtaLink,
    type HeaderNavLinks,
    type HeaderUserData,
} from '../Header';
import { GlassLogomark } from './GlassLogomark';

interface DesktopHeaderProps extends VariantProps<typeof _headerVariants> {
    /** The solid header's fill on a page that is not white. */
    groundClassName?: string;
    userData: HeaderUserData;
    primaryLinks: HeaderNavLinks;
    currentCountryCode: string;
    allowMemberGuides?: boolean;
    allowMemberOrders?: boolean;
    countries: HeaderCountryElement[];
    currentUrl: string;
    onLogout: () => void;
    onUserCartClick?: () => void;
    /**
     * Open the mini-cart on hover. Off where the cart icon opens a drawer
     * instead; defaults to the hover the legacy header has always had.
     */
    openCartMenuOnHover?: boolean;
    onLoginClick: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    warningMessages?: string[];
    isLegacyBrokenRemMode?: boolean;
    cartInfo: HeaderCartInfo;
    tagline?: string;
    primaryCta?: HeaderCtaLink;
    secondaryCta?: HeaderCtaLink;
    onPrimaryCtaClick?: () => void;
    /**
     * `true` draws the tall hero band with the oversized monogram in it.
     * `'space'` keeps that band and its nav layout but leaves the monogram out,
     * for a page whose own masthead already owns the picture — the premium
     * article, where a second mark sat over the banner's own title. Falsy
     * gives the compact band.
     */
    showLogomark?: boolean | 'space';
    /** Draw the mark as a pane of glass over the banner instead of a flat fill. */
    glassLogomark?: boolean;
    secondaryNavLinks?: HeaderNavLinks;
    /**
     * B6: when `true`, the header omits its own warning banner — the page
     * renders it in-flow itself (below the header, above the hero) so it
     * pushes content down instead of overlapping. Only set on pages that
     * render the banner inline in their own layout (e.g. product detail revamp
     * with a transparent/absolute header).
     */
    suppressWarningBanner?: boolean;
}

// The nav's dark CTA, on the same M step as the Sign Up button beside it —
// two pills of different heights in one row read as two different controls.
const _ctaPillClasses =
    'inline-flex h-[2.75rem] min-w-36 items-center justify-center whitespace-nowrap rounded-[var(--radius-btn-m)] bg-[#181818] px-4 py-2 text-sm font-bold leading-tight tracking-tight text-white shadow-sm transition-colors hover:bg-[#2a2a2a]';

// Standard Tailwind spacing classes throughout. Both Remix and BM consume
// Rocco's tailwind config, where the scale is generated 1-512 (with halfsteps
// under 160); BM additionally multiplies the resulting rem values by 16 so
// the same class renders at the same physical size under both root font
// settings. Arbitrary `px`/`rem` values would skip BM's compensation.

/**
 * How a nav link relates to where the reader is: `page` when the link is
 * this exact page, `section` when the page merely lives under it, `false`
 * otherwise. Handles the plural→singular route pairs the directory/detail
 * split uses (e.g. `/products` covers both `/products` and `/product/:slug`).
 *
 * Both truthy states light the marker, but only an exact match may say
 * `aria-current="page"` — that token asserts the link IS the current page,
 * so on a detail page it told a screen-reader reader they were on the
 * listing they were only underneath.
 */
const navLinkState = (
    linkHref: string,
    currentUrl: string,
): 'page' | 'section' | false => {
    if (!linkHref || linkHref === '#') return false;
    const normalize = (s: string) =>
        s.split('?')[0]?.split('#')[0]?.replace(/\/$/, '') ?? '';
    const link = normalize(linkHref);
    const path = normalize(currentUrl);
    if (link === '' || link === '/') {
        return path === '' || path === '/' ? 'page' : false;
    }
    if (path === link) return 'page';
    if (path.startsWith(`${link}/`)) return 'section';
    if (link.endsWith('s')) {
        const singular = link.slice(0, -1);
        if (path === singular || path.startsWith(`${singular}/`)) {
            return 'section';
        }
    }
    return false;
};

/**
 * B2B-aligned desktop header. Two layouts switched by `showLogomark`:
 *
 *  - **Tall** (`showLogomark === true`, `h-[23.625rem]`) — designed to overlay
 *    a hero. Rendered with a flex row: tagline + logomark on the left, nav +
 *    labelled search in the next column, horizontal country switcher to the
 *    right, then the right cluster (auth / conversion CTAs) at top-right.
 *
 *  - **Compact** (`showLogomark` falsy, `h-24`) — for non-hero routes. Same
 *    elements at a smaller scale using a 3-column grid (left content / centred
 *    wordmark / right cluster). The search collapses to an icon and the
 *    country switcher reverts to its popover dropdown.
 *
 * Both modes share the active/hover treatment for nav links and the same auth
 * / cart / user-menu plumbing. Mobile uses MobileHeader so this file only
 * styles the desktop breakpoint.
 */
export const DesktopHeader = (props: DesktopHeaderProps) => {
    const {
        color,
        userData,
        primaryLinks,
        currentCountryCode,
        allowMemberGuides,
        allowMemberOrders,
        countries,
        currentUrl,
        onLogout,
        onUserCartClick,
        openCartMenuOnHover,
        onLoginClick,
        additionalUserMenuItems,
        warningMessages,
        cartInfo,
        tagline,
        primaryCta,
        secondaryCta,
        onPrimaryCtaClick,
        showLogomark,
        glassLogomark,
        secondaryNavLinks,
        isLegacyBrokenRemMode,
        suppressWarningBanner,
    } = props;

    const isTransparent = color === 'transparent';
    const Logomark = isTransparent ? LogomarkWhite : LogomarkBlack;
    const menuColor: 'light' | 'dark' = isTransparent ? 'light' : 'dark';
    const mode = isTransparent ? 'float' : 'default';
    const linkColorClass = isTransparent
        ? 'text-white hover:text-white/80'
        : 'text-[#181818] hover:text-[#181818]/80';
    const mutedLinkColorClass = isTransparent
        ? 'text-[#9b9b9b] hover:text-white/80'
        : 'text-[#9b9b9b] hover:text-[#181818]/80';
    const iconColorClass = isTransparent
        ? 'fill-none stroke-white'
        : 'fill-none stroke-[#181818]';

    const [, setSearchClicked] = useAtom(uiStateAtomSearchClicked);
    const handleSearchClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        setSearchClicked(true);
        if (currentUrl.includes('/search')) {
            e.preventDefault();
        }
    };

    const renderArgs: RenderArgs = {
        tagline,
        primaryLinks,
        secondaryNavLinks,
        countries,
        currentCountryCode,
        currentUrl,
        linkColorClass,
        mutedLinkColorClass,
        iconColorClass,
        isTransparent,
        Logomark,
        handleSearchClick,
        userData,
        menuColor,
        onLogout,
        onUserCartClick,
        openCartMenuOnHover,
        onLoginClick,
        additionalUserMenuItems,
        allowMemberGuides,
        allowMemberOrders,
        cartInfo,
        primaryCta,
        secondaryCta,
        onPrimaryCtaClick,
        isLegacyBrokenRemMode,
        showLogomark,
        glassLogomark,
    };

    return (
        <header
            className={cn(
                _headerVariants({ color, mode }),
                // After the variant, so the page's ground wins over its
                // white.
                color === 'solid' && props.groundClassName,
                'rocco-header rocco-header-v2',
                // Hero-overlay (absolute) header sits below the site-wide top ad
                // banner. The solid `relative` header is already pushed down by
                // the in-flow banner, so it needs no offset. Falls back to 0px.
                mode === 'float' && 'top-(--ad-top-banner-height,0px)',
            )}
        >
            {/* Top scrim so the nav stays legible over light hero images —
                every transparent header, behind the content (-z-10, inside the
                header's stacking context so it stays above the hero). */}
            {isTransparent && (
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-40 bg-linear-to-b from-black/45 to-transparent"
                />
            )}
            {/* Always above the chrome so professional, project, and article
                (solid header) warnings sit at the top of the page. On hero-
                overlay headers, rendering after the layout lands mid-banner.
                Skipped when the page renders the banner in-flow itself
                (`suppressWarningBanner`). */}
            {!!warningMessages?.length && !suppressWarningBanner && (
                <div className="relative z-40 w-full qhd:mx-auto qhd:max-w-[1920px]">
                    <WarningBanner alerts={warningMessages} />
                </div>
            )}
            {/* One desktop layout for every page — the top row (tagline · nav ·
                wordmark · right actions) is identical everywhere; `showLogomark`
                only toggles the large "A" logomark and the taller hero band. */}
            {renderDesktopLayout(renderArgs)}
        </header>
    );
};

interface RenderArgs {
    tagline?: string;
    primaryLinks: HeaderNavLinks;
    secondaryNavLinks?: HeaderNavLinks;
    countries: HeaderCountryElement[];
    currentCountryCode: string;
    currentUrl: string;
    linkColorClass: string;
    mutedLinkColorClass: string;
    iconColorClass: string;
    isTransparent: boolean;
    Logomark?: React.FC<React.SVGProps<SVGSVGElement>>;
    handleSearchClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    userData: HeaderUserData;
    menuColor: 'light' | 'dark';
    onLogout: () => void;
    onUserCartClick?: () => void;
    /**
     * Open the mini-cart on hover. Off where the cart icon opens a drawer
     * instead; defaults to the hover the legacy header has always had.
     */
    openCartMenuOnHover?: boolean;
    onLoginClick: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    allowMemberGuides?: boolean;
    allowMemberOrders?: boolean;
    cartInfo: HeaderCartInfo;
    primaryCta?: HeaderCtaLink;
    secondaryCta?: HeaderCtaLink;
    onPrimaryCtaClick?: () => void;
    isLegacyBrokenRemMode?: boolean;
    /**
     * `true` draws the tall hero band with the oversized monogram in it.
     * `'space'` keeps that band and its nav layout but leaves the monogram out,
     * for a page whose own masthead already owns the picture — the premium
     * article, where a second mark sat over the banner's own title. Falsy
     * gives the compact band.
     */
    showLogomark?: boolean | 'space';
    /** Draw the mark as a pane of glass over the banner instead of a flat fill. */
    glassLogomark?: boolean;
}

/**
 * The single desktop header layout. The top row (centred wordmark + tagline /
 * nav columns + right-action cluster) is identical on every page; `showLogomark`
 * only adds the large "A" logomark and the taller hero band beneath it. Pages
 * without it get an auto-height band that fits the same top row.
 */
const renderDesktopLayout = (args: RenderArgs) => {
    const {
        tagline,
        primaryLinks,
        secondaryNavLinks,
        countries,
        currentCountryCode,
        currentUrl,
        linkColorClass,
        mutedLinkColorClass,
        iconColorClass,
        isTransparent,
        Logomark,
        handleSearchClick,
        userData,
        menuColor,
        onLogout,
        onUserCartClick,
        openCartMenuOnHover,
        onLoginClick,
        additionalUserMenuItems,
        allowMemberGuides,
        allowMemberOrders,
        cartInfo,
        primaryCta,
        secondaryCta,
        onPrimaryCtaClick,
        showLogomark,
        glassLogomark,
    } = args;

    return (
        <div
            className={cn(
                'relative flex w-full items-start pr-12 pl-12',
                'qhd:mx-auto qhd:max-w-[1920px]',
                // With the logomark: fixed tall hero band. Without it: auto
                // height that fits the same top row (top-anchored at pt-9) plus
                // bottom breathing room — so the nav sits identically either way.
                showLogomark ? 'h-94.5' : 'pb-9',
            )}
        >
            {/* Centred ARCHIPRO wordmark (Figma 997:33840) — links home. */}
            <RoccoLink
                to="/"
                prefetch="intent"
                aria-label="ArchiPro home"
                className="absolute top-12 left-1/2 -translate-x-1/2"
            >
                <ArchiproWordmark
                    aria-hidden
                    className={cn(
                        'h-6 w-auto',
                        isTransparent ? 'fill-white' : 'fill-[#141414]',
                    )}
                />
            </RoccoLink>
            {/* Column 1 — tagline + large logomark below. pt-9 mirrors the
                absolute version's `top-[2.25rem]` (36px from the header top). */}
            <div className="flex w-68 flex-col pt-9">
                <p
                    className={cn(
                        'm-0! text-4! leading-tight! font-medium tracking-[-0.03em]',
                        linkColorClass,
                    )}
                >
                    {tagline ?? <DefaultRichTagline />}
                </p>
                {/* Figma 997:33839 — 166px-tall mark, top edge 210px from the
                    header top (tagline block ends at 74px → 136px gap).
                    `showLogomark` only toggles this mark; everything else in
                    the header is unchanged (BC-4488). */}
                {/* `'space'` keeps the band above without drawing in it. */}
                {showLogomark === true &&
                    (glassLogomark ? (
                        <GlassLogomark className="mt-34 -ml-16 h-41.5" />
                    ) : (
                        Logomark && (
                            <Logomark
                                aria-hidden
                                className="pointer-events-none mt-34 -ml-16 h-41.5 w-auto"
                            />
                        )
                    ))}
            </div>

            {/* Column 2 — vertical nav with optional muted secondary list,
                then a labelled Search link beneath. pt-9 mirrors the absolute
                `top-[2.1875rem]` (35px ≈ 36px on the standard scale). */}
            {/* 530.55px from the frame edge: 48 (pl) + 272 (col 1) + 210. */}
            <div className="ml-49 flex w-36 flex-col pt-9">
                <ul className="m-0 flex flex-col gap-y-px p-0">
                    {primaryLinks.links.map((link) => (
                        <NavListItem
                            key={`primary-${link.title}-${link.to}`}
                            link={link}
                            currentUrl={currentUrl}
                            colorClass={linkColorClass}
                        />
                    ))}
                    {secondaryNavLinks?.links.map((link) => (
                        <NavListItem
                            key={`secondary-${link.title}-${link.to}`}
                            link={link}
                            currentUrl={currentUrl}
                            colorClass={mutedLinkColorClass}
                        />
                    ))}
                </ul>
            </div>

            {/* Right cluster — single vertically-centred row matching Figma
                `997:33835`: search · globe+country · Become a Pro · Log In ·
                white Sign Up. `ml-auto` pushes it right; `pt-6` (24px) mirrors
                the design's `top-[1.5rem]`.

                When `primaryCta` is set (B2B conversion mode) the cluster is
                just the CTA, so the account / search / country chrome drops. */}
            <div className="ml-auto flex items-center gap-7.5 pt-9">
                {primaryCta ? (
                    <>
                        <PrimaryCta
                            cta={primaryCta}
                            onClick={onPrimaryCtaClick}
                        />
                        {secondaryCta && (
                            <RoccoLink
                                to={secondaryCta.href ?? '#'}
                                className={cn(
                                    'text-sm font-bold tracking-tight hover:underline',
                                    linkColorClass,
                                )}
                            >
                                {secondaryCta.label}
                            </RoccoLink>
                        )}
                    </>
                ) : (
                    <>
                        <RoccoLink
                            to="/search"
                            prefetch="intent"
                            onClick={handleSearchClick}
                            aria-label="Search"
                        >
                            <SearchIcon
                                className={cn(
                                    'size-6 stroke-2',
                                    iconColorClass,
                                )}
                            />
                        </RoccoLink>
                        <CountrySelectMenu
                            color={menuColor}
                            currentCountryCode={currentCountryCode}
                            countries={countries}
                        />
                        {userData.isAuthorized ? (
                            <>
                                <UserCart
                                    fill={
                                        menuColor === 'dark' ? 'dark' : 'light'
                                    }
                                    openCartMenuOnHover={openCartMenuOnHover}
                                    onUserCartClick={() => {
                                        onUserCartClick?.();
                                    }}
                                    cartInfo={cartInfo}
                                />
                                <DesktopUserMenu
                                    userData={userData}
                                    color={menuColor}
                                    onLogout={onLogout}
                                    additionalUserMenuItems={
                                        additionalUserMenuItems
                                    }
                                    allowMemberGuides={allowMemberGuides}
                                    allowMemberOrders={allowMemberOrders}
                                />
                            </>
                        ) : (
                            <>
                                {cartInfo.cartCount > 0 && (
                                    <UserCart
                                        fill={
                                            menuColor === 'dark'
                                                ? 'dark'
                                                : 'light'
                                        }
                                        openCartMenuOnHover={
                                            openCartMenuOnHover
                                        }
                                        onUserCartClick={() => {
                                            onUserCartClick?.();
                                        }}
                                        cartInfo={cartInfo}
                                    />
                                )}
                                {/* The design doubles the gap (≈60px) before
                                    Become a Pro and Log in; ml-7.5 stacks on
                                    the cluster's gap-7.5. */}
                                {/* 16px / 500, letter-spacing 0, per the
                                    design's Button/Text token. */}
                                <RoccoLink
                                    to="/become-a-pro"
                                    className={cn(
                                        'ml-7.5 text-base font-medium whitespace-nowrap hover:underline',
                                        linkColorClass,
                                    )}
                                >
                                    Become a Pro
                                </RoccoLink>
                                <button
                                    type="button"
                                    onClick={onLoginClick}
                                    aria-label="Log In"
                                    className={cn(
                                        'ml-7.5 text-base font-medium hover:underline',
                                        linkColorClass,
                                    )}
                                >
                                    Log In
                                </button>
                                {/* Sign Up — #f2f2f2, sized to the platform's
                                    M step so it reads as the same control as
                                    the Visit site button on a tile: 2.75rem
                                    tall, and the ramp's M corner, which is the
                                    1rem that step carries. Opens the auth modal
                                    via `onLoginClick`. */}
                                <button
                                    type="button"
                                    onClick={onLoginClick}
                                    className="flex h-[2.75rem] w-32.5 items-center justify-center rounded-[var(--radius-btn-m)] bg-[#f2f2f2] text-base font-medium whitespace-nowrap text-[#141414] transition-colors hover:bg-white"
                                >
                                    Sign Up
                                </button>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * One nav row. Reserves a fixed-width slot for the active marker so every link
 * starts at the same x coordinate whether or not the triangle is rendered.
 */
const NavListItem = ({
    link,
    currentUrl,
    colorClass,
}: {
    link: HeaderNavLinks['links'][number];
    currentUrl: string;
    colorClass: string;
}) => {
    const state = navLinkState(link.to, currentUrl);
    const isActive = state !== false;
    return (
        <li className="relative flex items-center whitespace-nowrap">
            <ActiveMarker className={cn('mr-1.5', !isActive && 'invisible')} />
            <RoccoLink
                to={link.to}
                prefetch="intent"
                onClick={link.onClick}
                isNonRemixRoute={link.isNonRemixRoute}
                aria-current={
                    state === 'page'
                        ? 'page'
                        : state === 'section'
                          ? true
                          : undefined
                }
                className={cn(
                    // The design face renders lighter than ours, so the nav
                    // links sit at 500 to visually match.
                    'text-base leading-tight font-medium tracking-[-0.03em] hover:underline',
                    colorClass,
                )}
            >
                {link.title}
            </RoccoLink>
        </li>
    );
};

// Inline tagline links inherit the surrounding `<p>` colour explicitly via
// `text-current` so legacy BM's global `a {}` styles can't repaint them.
// `hover:text-current` does the same for the hover state — legacy BM has a
// global `a:hover { color: <gold> }` (legacy-base/_scaffolding.scss) that would
// otherwise tint these gold; the higher-specificity hover utility keeps them
// black and is a no-op on the public site.
const _taglineLinkClasses =
    'text-current no-underline hover:text-current hover:underline';

const DefaultRichTagline = () => (
    <>
        Browse{' '}
        <RoccoLink to="/projects" className={_taglineLinkClasses}>
            Projects
        </RoccoLink>
        , find{' '}
        <RoccoLink to="/products" className={_taglineLinkClasses}>
            Products
        </RoccoLink>
        ,<br aria-hidden />
        connect with{' '}
        <RoccoLink to="/professionals" className={_taglineLinkClasses}>
            Professionals
        </RoccoLink>
        .
    </>
);

const PrimaryCta = ({
    cta,
    onClick,
}: {
    cta: HeaderCtaLink;
    onClick?: () => void;
}) => {
    if (cta.opensModal && onClick) {
        return (
            <button type="button" onClick={onClick} className={_ctaPillClasses}>
                {cta.label}
            </button>
        );
    }
    const href = cta.href ?? '#';
    const isExternal = /^https?:\/\//i.test(href);
    return (
        <RoccoLink
            to={href}
            isNonRemixRoute={isExternal}
            newTab={isExternal}
            className={_ctaPillClasses}
        >
            {cta.label}
        </RoccoLink>
    );
};
